// src/pages/public/Careers/CareerDetail/CareerDetailPage.jsx
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { openModal } from '../../../../redux/slices/modalSlice.js';
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaClock,
  FaBriefcase,
  FaRegCalendarCheck
} from 'react-icons/fa'

import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppDispatch.js'
import { fetchJobBySlug } from '../../../../redux/slices/jobsSlice.js'
import SEO from '../../../../components/seo/SEO.jsx'
// import CareerApplyForm from '../../../../components/forms/CareerApplyForm.jsx'
import { LazyImage } from '../../../../components/common/LazyImage.jsx'
import ErrorBlock from '../../../../components/common/ErrorBlock.jsx'
import SkeletonSection from '../../../../components/common/SkeletonSection.jsx'
import Modal from '../../../../components/common/Modal.jsx'

import './CareerDetailPage.scss'

export default function CareerDetailPage() {
  const { slug } = useParams()
  const dispatch = useAppDispatch()
  const { currentJob: job, loading, error } = useAppSelector((state) => state.jobs)
  const [showApplyModal, setShowApplyModal] = useState(false)

  const handleOpenContactModal = () => {
    dispatch(openModal({
      modalType: 'job_apply',
      modalProps: {
        jobPosition: job.hero?.heading,
        jobId: job?._id
      }
    }));
  };

  useEffect(() => {
    if (slug) {
      dispatch(fetchJobBySlug(slug))
    }
  }, [dispatch, slug])

  if (loading) {
    return (
      <>
        <SEO title="Loading Job..." />
        <div className="space-y-12">
          <SkeletonSection />
          <SkeletonSection />
          <SkeletonSection />
        </div>
      </>
    )
  }

  if (error || !job) {
    return (
      <>
        <SEO title="Job Not Found" />
        <div className="container-custom section-padding">
          <ErrorBlock
            title="Job Not Found"
            message="The job position you're looking for doesn't exist or has been filled."
            onRetry={() => slug && dispatch(fetchJobBySlug(slug))}
          />
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title={job.seoTitle || job.title}
        description={job.seoDescription || `Apply for ${job.title} position at LogiQlink`}
        type="article"
      />

      <div className="career-detail-page-root">
        <HeroSection job={job} />
        <JobDetailsSection
          job={job}
          onOpenApply={() => handleOpenContactModal()}
        />
      </div>

      {/* Apply Modal */}
      {/* <Modal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        title={`Apply for ${job.title}`}
        size="large"
      >
        <CareerApplyForm
          jobId={job._id}
          jobTitle={job.title}
        />
      </Modal> */}
    </>
  )
}

/* =========================
   Hero Section (dark v1 style)
   ========================= */

function HeroSection({ job }) {
  const hero = job.hero || {}

  const mapSpanToCapsule = (span) => {
    const text = span?.value || ''
    const lower = text.toLowerCase()

    if (lower.includes('remote') || lower.includes('on-site') || lower.includes('onsite') || lower.includes('hybrid')) {
      return { icon: <FaMapMarkerAlt />, text }
    } else if (lower.includes('full-time') || lower.includes('part-time') || lower.includes('contract')) {
      return { icon: <FaClock />, text }
    } else if (lower.includes('engineer') || lower.includes('developer') || lower.includes('designer') || lower.includes('hr')) {
      return { icon: <FaBriefcase />, text }
    } else if (lower.includes('years') || lower.includes('experience') || lower.includes('months')) {
      return { icon: <FaRegCalendarCheck />, text }
    } else {
      return { icon: <FaBriefcase />, text }
    }
  }

  const capsules = hero.spans && hero.spans.length
    ? hero.spans.map(mapSpanToCapsule)
    : [
      { icon: <FaMapMarkerAlt />, text: 'Location not specified' },
      { icon: <FaClock />, text: 'Full-time' },
      { icon: <FaBriefcase />, text: job.title || 'Role' },
      { icon: <FaRegCalendarCheck />, text: 'Experience not specified' }
    ]

  const handleBack = () => {
    window.history.back()
  }

  return (
    <section className="career-details-hero">
      <div className="career-details-container">
        {/* Back Button */}
        <motion.button
          className="back-button"
          onClick={handleBack}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.98 }}
        >
          <FaArrowLeft className="back-icon" />
          Back to Careers
        </motion.button>

        {/* Main Content */}
        <div className="career-content">
          <motion.h1
            className="career-heading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {(hero.heading || job.title || 'Position Title').toUpperCase()}
          </motion.h1>

          <motion.div
            className="capsules-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {capsules.map((capsule, index) => (
              <motion.div
                key={index}
                className="info-capsule"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.4,
                  delay: 0.6 + index * 0.1
                }}
                whileHover={{
                  scale: 1.05,
                  backgroundColor: 'var(--primary)',
                  color: '#000'
                }}
              >
                <span className="capsule-icon">{capsule.icon}</span>
                <span className="capsule-text">{capsule.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  )
}

/* =========================
   Job Details Section (About + Details + Apply)
   ========================= */

function JobDetailsSection({ job, onOpenApply }) {
  const about = job.about
  const details = job.details
  const bottomSection = job.bottomSection

  return (
    <section className="job-details-section">
      <div className="job-details-container">
        {/* About Section */}
        {about?.isVisible !== false && (about?.heading || about?.description) && (
          <motion.div
            className="about-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {about.heading && (
              <h2 className="section-heading">{about.heading}</h2>
            )}
            {about.description && (
              <p className="section-description">{about.description}</p>
            )}
          </motion.div>
        )}

        {/* Details Grid */}
        {details?.isVisible !== false &&
          details?.cards &&
          details.cards.length > 0 && (
            <div className="details-grid">
              {details.cards.map((card, index) => (
                <motion.div
                  key={index}
                  className="detail-column"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                >
                  {card.title && (
                    <h3 className="column-heading">{card.title}</h3>
                  )}

                  {card.list && card.list.length > 0 && (
                    <ul className="detail-list">
                      {card.list.map((item, itemIndex) => (
                        <motion.li
                          key={itemIndex}
                          className="list-item"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: itemIndex * 0.05 }}
                        >
                          {item}
                        </motion.li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              ))}
            </div>
          )}

        {/* Application Section (Bottom) */}
        {bottomSection?.isVisible !== false && (
          <motion.div
            className="application-section"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="section-heading">
              {bottomSection.Heading || 'How to Apply'}
            </h2>

            {bottomSection.Subheading && (
              <p className="section-description">
                {bottomSection.Subheading}
              </p>
            )}

            <motion.button
              className="apply-button"
              onClick={onOpenApply}
              whileHover={{
                scale: 1.05,
                backgroundColor: 'var(--primary)',
                color: '#000'
              }}
              whileTap={{ scale: 0.98 }}
            >
              {bottomSection.buttonText || 'Apply Now'}
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  )
}
